import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";

import {
  Business,
  Email,
  Phone,
  LocationCity,
  Edit,
  Delete,
  Mail,
  GroupAdd,
} from "@mui/icons-material";
import AppButton from "../utils/Appbutton";
import { useNavigate } from "react-router-dom";
import {
  createClientAccess,
  deleteClientAccess,
  editClientAccess,
  getClientAccesses,
  sendEmail,
} from "../services/source";
import auth from "../services/auth";

const ClientCard = ({ client, onEdit }) => {
  const navigate = useNavigate();
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertSeverity, setAlertSeverity] = React.useState("success");
  const [openAccessModal, setOpenAccessModal] = React.useState(false);
  const [accessLoading, setAccessLoading] = React.useState(false);
  const [accessForm, setAccessForm] = React.useState({
    nome: "",
    email: "",
    password: "",
  });
  const [clientAccesses, setClientAccesses] = React.useState([]);
  const [openEditAccessModal, setOpenEditAccessModal] = React.useState(false);
  const [openDeleteAccessModal, setOpenDeleteAccessModal] =
    React.useState(false);
  const [selectedAccess, setSelectedAccess] = React.useState(null);
  const [editAccessForm, setEditAccessForm] = React.useState({
    nome: "",
    email: "",
    password: "",
  });

  const user = auth.getCurrentUser();
  const companyId = user?.data?.companyId || user?.data?.id;
  const principalClientId =
    client?.clientRootId || client?.principalClientId || client?.id;

  const loadClientAccesses = React.useCallback(async () => {
    if (!principalClientId || !companyId) return;
    const data = await getClientAccesses({
      clientId: principalClientId,
      companyId,
    });
    setClientAccesses(Array.isArray(data) ? data : []);
  }, [principalClientId, companyId]);

  React.useEffect(() => {
    loadClientAccesses();
  }, [loadClientAccesses]);

  const handleSendEmail = async () => {
    const email = client?.email?.trim();
    const nome = client?.nome || "";
    const password = client?.password || "";

    if (!email) {
      setAlertSeverity("error");
      setAlertMessage("Email do cliente não encontrado.");
      return;
    }

    const res = await sendEmail({
      email,
      nome,
      password,
      clientId: principalClientId,
      companyId,
    });

    if (res?.status === 200) {
      setAlertSeverity("success");
      setAlertMessage("Email enviado com sucesso.");
    } else {
      setAlertSeverity("error");
      setAlertMessage(
        res?.data?.message ||
          "Erro ao enviar email. Verifique a configuração do servidor.",
      );
    }

    setAlertOpen(true);
  };

  const handleAccessField = (field) => (event) => {
    setAccessForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreateClientAccess = async () => {
    if (!accessForm.nome || !accessForm.email || !accessForm.password) {
      setAlertSeverity("error");
      setAlertMessage("Preencha nome, email e senha.");
      setAlertOpen(true);
      return;
    }

    if (accessForm.password.length < 6) {
      setAlertSeverity("error");
      setAlertMessage("Senha deve ter no mínimo 6 caracteres.");
      setAlertOpen(true);
      return;
    }

    try {
      setAccessLoading(true);
      await createClientAccess({
        clientId: principalClientId,
        companyId,
        nome: accessForm.nome,
        email: accessForm.email,
        password: accessForm.password,
      });

      setAlertSeverity("success");
      setAlertMessage("Acesso de cliente criado com sucesso.");
      setAlertOpen(true);
      setAccessForm({ nome: "", email: "", password: "" });
      setOpenAccessModal(false);
      await loadClientAccesses();
    } catch (err) {
      setAlertSeverity("error");
      setAlertMessage(
        err?.response?.data?.message || "Erro ao criar acesso do cliente.",
      );
      setAlertOpen(true);
    } finally {
      setAccessLoading(false);
    }
  };

  const handleOpenEditAccess = (access) => {
    setSelectedAccess(access);
    setEditAccessForm({
      nome: access?.nome || "",
      email: access?.email || "",
      password: "",
    });
    setOpenEditAccessModal(true);
  };

  const handleEditAccessField = (field) => (event) => {
    setEditAccessForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditClientAccess = async () => {
    if (!selectedAccess?.id) {
      setAlertSeverity("error");
      setAlertMessage("Acesso inválido.");
      setAlertOpen(true);
      return;
    }

    if (!editAccessForm.nome || !editAccessForm.email) {
      setAlertSeverity("error");
      setAlertMessage("Preencha nome e email.");
      setAlertOpen(true);
      return;
    }

    if (editAccessForm.password && editAccessForm.password.length < 6) {
      setAlertSeverity("error");
      setAlertMessage("Senha deve ter no mínimo 6 caracteres.");
      setAlertOpen(true);
      return;
    }

    try {
      setAccessLoading(true);
      await editClientAccess({
        clientId: principalClientId,
        companyId,
        accessId: selectedAccess.id,
        nome: editAccessForm.nome,
        email: editAccessForm.email,
        password: editAccessForm.password,
      });

      setAlertSeverity("success");
      setAlertMessage("Acesso de cliente atualizado com sucesso.");
      setAlertOpen(true);
      setOpenEditAccessModal(false);
      setSelectedAccess(null);
      setEditAccessForm({ nome: "", email: "", password: "" });
      await loadClientAccesses();
    } catch (err) {
      setAlertSeverity("error");
      setAlertMessage(
        err?.response?.data?.message || "Erro ao atualizar acesso do cliente.",
      );
      setAlertOpen(true);
    } finally {
      setAccessLoading(false);
    }
  };

  const handleRequestDeleteAccess = (access) => {
    setSelectedAccess(access);
    setOpenDeleteAccessModal(true);
  };

  const handleDeleteClientAccess = async () => {
    if (!selectedAccess?.id) {
      setAlertSeverity("error");
      setAlertMessage("Acesso inválido.");
      setAlertOpen(true);
      return;
    }

    try {
      setAccessLoading(true);
      await deleteClientAccess({
        clientId: principalClientId,
        companyId,
        accessId: selectedAccess.id,
      });

      setAlertSeverity("success");
      setAlertMessage("Acesso de cliente removido com sucesso.");
      setAlertOpen(true);
      setOpenDeleteAccessModal(false);
      setSelectedAccess(null);
      await loadClientAccesses();
    } catch (err) {
      setAlertSeverity("error");
      setAlertMessage(
        err?.response?.data?.message || "Erro ao remover acesso do cliente.",
      );
      setAlertOpen(true);
    } finally {
      setAccessLoading(false);
    }
  };

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 5,
        transition: "0.3s",
        height: "100%",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 10px 20px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#9c27b0",
                width: 56,
                height: 56,
              }}
            >
              {client.nome.charAt(0)}
            </Avatar>

            <Box>
              <Typography variant="h6" fontWeight="bold">
                {client.nome
                  ?.trim()
                  .split(" ")
                  .filter(Boolean)
                  .map((name, index, array) =>
                    index === 0 || index === array.length - 1 ? name : null,
                  )
                  .filter(Boolean)
                  .join(" ")}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {client.empresa}
              </Typography>
            </Box>
          </Box>

          {/* Info */}
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Email fontSize="small" color="primary" />
              <Typography variant="body2">{client.email}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Phone fontSize="small" color="primary" />
              <Typography variant="body2">{client.telemovel}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationCity fontSize="small" color="primary" />
              <Typography variant="body2">{client.cidade}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Business fontSize="small" color="primary" />
              <Chip
                label={client.status}
                color={client.status === "Ativo" ? "success" : "error"}
                size="small"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Editar</Typography>
              <IconButton
                aria-label="edit"
                size="medium"
                onClick={() => onEdit?.(client)}
                sx={{
                  backgroundColor: "#0664cf",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#5188db",
                  },
                }}
              >
                <Edit fontSize="inherit" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Enviar</Typography>
              <IconButton
                aria-label="mail"
                size="medium"
                onClick={handleSendEmail}
                sx={{
                  backgroundColor: "#1e9004",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#79ee8e",
                  },
                }}
              >
                <Mail fontSize="inherit" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Acesso</Typography>
              <IconButton
                aria-label="create-client-access"
                size="medium"
                onClick={() => setOpenAccessModal(true)}
                sx={{
                  backgroundColor: "#7b1fa2",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#9c27b0",
                  },
                }}
              >
                <GroupAdd fontSize="inherit" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2">Acessos:</Typography>
              {clientAccesses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sem acessos
                </Typography>
              ) : (
                clientAccesses.map((access) => (
                  <Box
                    key={access.id}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Chip
                      label={access.nome}
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditAccess(access)}
                      sx={{ p: 0.5, color: "success.main" }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRequestDeleteAccess(access)}
                      sx={{ p: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Box>
          </Stack>
        </Stack>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <AppButton
            minWidth={90}
            height={40}
            type="button"
            variant="contained"
            size="small"
            valueText="Selecionar"
            onClick={() => navigate("/clientDashboard", { state: { client } })}
          />
        </Box>
      </CardContent>
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={openAccessModal}
        onClose={() => !accessLoading && setOpenAccessModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Criar acesso para cliente</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Nome"
              value={accessForm.nome}
              onChange={handleAccessField("nome")}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              type="email"
              value={accessForm.email}
              onChange={handleAccessField("email")}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Senha"
              type="password"
              value={accessForm.password}
              onChange={handleAccessField("password")}
              helperText="Mínimo de 6 caracteres"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenAccessModal(false)}
            disabled={accessLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateClientAccess}
            variant="contained"
            disabled={accessLoading}
          >
            {accessLoading ? "A criar..." : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openEditAccessModal}
        onClose={() => !accessLoading && setOpenEditAccessModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar acesso do cliente</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Nome"
              value={editAccessForm.nome}
              onChange={handleEditAccessField("nome")}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              type="email"
              value={editAccessForm.email}
              onChange={handleEditAccessField("email")}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Nova senha (opcional)"
              type="password"
              value={editAccessForm.password}
              onChange={handleEditAccessField("password")}
              helperText="Deixe vazio para manter a senha atual"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenEditAccessModal(false)}
            disabled={accessLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditClientAccess}
            variant="contained"
            disabled={accessLoading}
          >
            {accessLoading ? "A guardar..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteAccessModal}
        onClose={() => !accessLoading && setOpenDeleteAccessModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remover acesso</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Tem certeza que deseja remover o acesso de {selectedAccess?.nome}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteAccessModal(false)}
            disabled={accessLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteClientAccess}
            color="error"
            variant="contained"
            disabled={accessLoading}
          >
            {accessLoading ? "A remover..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ClientCard;
